// Constants for Expiration and Freeze Thaw static schema interfaces (SSI).

import moment from 'moment';

import notifications from 'notifications';
import {ImplementedStaticSchemaInterface} from 'schemas/SchemaConstants';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {DATETIME_FORMATS} from 'util/DateConstants';
import {isValidDatetime} from 'util/DateUtil';

// Keep in sync with benchling/inventory/valid_contents/domain/constants.py
export const EXPIRATION_TRACKING_ENTITY_SCHEMA_INTERFACE_ID = 'ssi_expirationtracking';
export const EXPIRATION_TRACKING_ICON: BenchlingIconName = 'calendar-days';
export const EXPIRATION_DATE_FIELD_DISPLAY_NAME = 'Expiration date';
export const FREEZE_THAW_TRACKING_SCHEMA_INTERFACE_ID = 'ssi_freezethawtracking';

// Additional features for freeze-thaw
export interface FreezeThawStaticSchemaInterface extends ImplementedStaticSchemaInterface {
  limit: number | null;
}

export function isFreezeThawInterfaceImplementation(
  ssi: ImplementedStaticSchemaInterface
): ssi is FreezeThawStaticSchemaInterface {
  return ssi.interfaceId === FREEZE_THAW_TRACKING_SCHEMA_INTERFACE_ID;
}

// Parsing and rendering Expiration Datetime (the API should be able to handle most ISO 8601 and RFC 3339)
// Prefer to render expiration datetime without seconds in the UI.
export const EXPIRATION_DATETIME_PRESENTATIONAL_FORMAT = 'YYYY-MM-DD hh:mm A';
export const EXPIRATION_DATETIME_FORMATS = [...DATETIME_FORMATS, EXPIRATION_DATETIME_PRESENTATIONAL_FORMAT];

interface UpdateExpirationDateOverride {
  // If false, do not update the Container expiration_date_override. If true, either set or clear it.
  doUpdate: boolean;
  // Only used if doUpdate is true. If null, clear the expiration_date_override. If populated, the string
  // will be a valid datetime with a timezone offset.
  expirationDateOverride: string | null;
}

/**
 * Parse the user-input expiration date string to use for updating Container expiration_date_override.
 *
 * @param inputDatetime a valid datetime string (see EXPIRATION_DATETIME_FORMATS), or null/empty if the
 *        expiration_datetime_override should be cleared.
 * @param previousDatetime the previous expiration_datetime_override value to compare to, which should also be
 *        a valid datetime string (see EXPIRATION_DATETIME_FORMATS). If inputDatetime represents the same
 *        logical datetime value, update should not happen. If not provided or null, this check is skipped.
 * @returns whether to update the expiration_date_override and with what value. See
 *        UpdateExpirationDateOverride for details.
 */
export function validateAndConvertExpirationDatetime(
  inputDatetime: string | null,
  previousDatetime?: string | null
): UpdateExpirationDateOverride {
  // Date input components use both an empty string and null to represent no date.
  if (!inputDatetime || inputDatetime.trim() === '') {
    return {doUpdate: true, expirationDateOverride: null};
  }
  inputDatetime = inputDatetime.trim();
  // Datetime is in "YYYY-MM-DD hh:mm AM/PM" format by default, but the user could have edited the text
  // input directly without the picker.
  if (
    !isValidDatetime({
      textValue: inputDatetime,
      validDatetimeFormats: EXPIRATION_DATETIME_FORMATS,
    })
  ) {
    notifications.error('Invalid datetime');
    return {doUpdate: false, expirationDateOverride: null};
  }
  const inputMoment = moment(inputDatetime);
  if (previousDatetime) {
    // Assume the previous datetime has a valid format (it wasn't user-edited). Do not update if the
    // expiration date did not change (ignore seconds); this avoids "locking in" an inherited expiration date
    // as an override, and it reduces unnecessary updates from differing precision.
    const previousMoment = moment(previousDatetime);
    if (inputMoment.isSame(previousMoment, 'minute')) {
      return {doUpdate: false, expirationDateOverride: null};
    }
  }
  // Convert datetime to ISO format to include the timezone offset
  return {doUpdate: true, expirationDateOverride: inputMoment.format()};
}
